import React, { useState, useEffect } from "react";
import API from "../services/api";
import { printNote } from "./PrintPDFUtils";
import { todayEastern } from "../dateUtils";

/* ===============================
   MODAL: CREATE NEW COLLATERAL CONTACT NOTE
   =============================== */
function CollateralContactModal({ isOpen, onClose, onSubmit, clientId, clientName }) {
  if (!isOpen) return null;

  const [date, setDate] = useState(todayEastern());
  const [contactType, setContactType] = useState("Phone Call");
  const [contactWith, setContactWith] = useState("");
  const [contactDuration, setContactDuration] = useState("");
  const [contactPurpose, setContactPurpose] = useState("");
  const [contactSummary, setContactSummary] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [permissionToContact, setPermissionToContact] = useState(true);
  
  const handleSave = () => {
    const contactNoteObj = {
      type: "CollateralContact",
      date,
      contactType,
      contactWith,
      contactDuration,
      contactPurpose,
      contactSummary,
      followUp,
      permissionToContact
    };
    onSubmit(contactNoteObj);
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>New Collateral Contact Note</h2>
        
        <div style={styles.clientInfo}>
          <strong>Client:</strong> {clientName || "Selected Client"} | <strong>Date:</strong> {date}
          <button
            onClick={() => setDate(todayEastern())}
            style={styles.smallButton}
          >
            Set Today
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.dateInput}
          />
        </div>
        
        <div style={styles.formScrollArea}>
          {/* Contact Type */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Type of Contact:</h3>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value)}
              style={styles.select}
            >
              <option value="Phone Call">Phone Call</option>
              <option value="Email">Email</option>
              <option value="Text Message">Text Message</option>
              <option value="Video Call">Video Call</option>
              <option value="In-Person Meeting">In-Person Meeting</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Contact With */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Contact With:</h3>
            <div style={styles.fieldGroup}>
              <input
                type="text"
                value={contactWith}
                onChange={(e) => setContactWith(e.target.value)}
                style={styles.input}
                placeholder="Name and relationship to client (e.g., Dr. Smith - Primary Care Physician)"
              />
            </div>
          </div>

          {/* Permission */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Client Permission:</h3>
            <div style={styles.checkboxItem}>
              <input
                type="checkbox"
                id="permissionCheck"
                checked={permissionToContact}
                onChange={(e) => setPermissionToContact(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              <label htmlFor="permissionCheck">
                Client has given permission for this communication
              </label>
            </div>
          </div>

          {/* Duration */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Duration:</h3>
            <div style={styles.fieldGroup}>
              <input
                type="text"
                value={contactDuration}
                onChange={(e) => setContactDuration(e.target.value)}
                style={styles.input}
                placeholder="Duration of contact (e.g., 15 minutes)"
              />
            </div>
          </div>

          {/* Purpose */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Purpose of Contact:</h3>
            <textarea
              rows="3"
              value={contactPurpose}
              onChange={(e) => setContactPurpose(e.target.value)}
              style={styles.textArea}
              placeholder="Document the purpose of this contact..."
            />
          </div>

          {/* Summary */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Summary of Contact:</h3>
            <textarea
              rows="5"
              value={contactSummary}
              onChange={(e) => setContactSummary(e.target.value)}
              style={styles.textArea}
              placeholder="Document what was discussed or communicated..."
            />
          </div>

          {/* Follow-up */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Follow-up Required:</h3>
            <textarea
              rows="3"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              style={styles.textArea}
              placeholder="Document any required follow-up actions..."
            />
          </div>
        </div>

        <div style={styles.modalButtons}>
          <button onClick={handleSave} style={styles.saveBtn}>
            Save
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===============================
   MODAL: VIEW/EDIT EXISTING COLLATERAL CONTACT NOTE
   =============================== */
function CollateralContactViewModal({ isOpen, onClose, note, onUpdate, onSendAsPDF }) {
  if (!isOpen || !note) return null;

  // Parse
  let parsed = {};
  try {
    parsed = JSON.parse(note.notes);
  } catch (err) {
    console.error("Error parsing note:", err);
  }

  const [editMode, setEditMode] = useState(false);

  // Copy fields from `parsed`
  const [date, setDate] = useState(parsed.date || "");
  const [contactType, setContactType] = useState(parsed.contactType || "Phone Call");
  const [contactWith, setContactWith] = useState(parsed.contactWith || "");
  const [contactDuration, setContactDuration] = useState(parsed.contactDuration || "");
  const [contactPurpose, setContactPurpose] = useState(parsed.contactPurpose || "");
  const [contactSummary, setContactSummary] = useState(parsed.contactSummary || "");
  const [followUp, setFollowUp] = useState(parsed.followUp || "");
  const [permissionToContact, setPermissionToContact] = useState(
    parsed.permissionToContact !== undefined ? parsed.permissionToContact : true
  );
  
  // Store client name from note for printing
  const [clientName, setClientName] = useState("");
  
  // Fetch client name on load
  useEffect(() => {
    if (note && note.client_id) {
      const fetchClientName = async () => {
        try {
          const res = await API.get(`/therapist/clients/${note.client_id}`);
          if (res.data && res.data.client_name) {
            setClientName(res.data.client_name);
          }
        } catch (err) {
          console.error("Error fetching client name:", err);
        }
      };
      
      fetchClientName();
    }
  }, [note]);

  const handleSave = () => {
    const updatedContactNote = {
      type: "CollateralContact",
      date,
      contactType,
      contactWith,
      contactDuration,
      contactPurpose,
      contactSummary,
      followUp,
      permissionToContact
    };
    onUpdate(note.id, updatedContactNote);
    setEditMode(false);
  };
  
  // Handle printing
  const handlePrint = () => {
    const noteData = {
      type: "CollateralContact",
      date,
      contactType,
      contactWith,
      contactDuration,
      contactPurpose,
      contactSummary,
      followUp,
      permissionToContact
    };
    
    printNote({notes: JSON.stringify(noteData)}, "Collateral Contact Note", clientName);
  };

  // Helper to render form fields based on editMode
  const renderField = (field, setField, fieldType = "text", options = null) => {
    if (editMode) {
      if (fieldType === "textarea") {
        return (
          <textarea
            rows="3"
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.textArea}
          />
        );
      } else if (fieldType === "select" && options) {
        return (
          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.select}
          >
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      } else if (fieldType === "checkbox") {
        return (
          <input
            type="checkbox"
            checked={field}
            onChange={(e) => setField(e.target.checked)}
            style={{ marginLeft: "8px" }}
          />
        );
      } else {
        return (
          <input
            type="text"
            value={field}
            onChange={(e) => setField(e.target.value)}
            style={styles.input}
          />
        );
      }
    } else {
      if (fieldType === "checkbox") {
        return <span style={styles.readOnly}>{field ? "Yes" : "No"}</span>;
      }
      return <div style={styles.readOnly}>{field}</div>;
    }
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>
          {editMode ? "Edit Collateral Contact Note" : "View Collateral Contact Note"}
        </h2>

        <div style={{ textAlign: "right", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          {!editMode && (
            <>
              <button
                onClick={handlePrint}
                style={{ ...styles.button, backgroundColor: "var(--text-muted)" }}
              >
                Print/PDF
              </button>
              <button
                onClick={() => setEditMode(true)}
                style={{ ...styles.button, backgroundColor: "#ffc107" }}
              >
                Edit
              </button>
            </>
          )}
          {onSendAsPDF && (
            <button onClick={() => onSendAsPDF(note)} style={{ ...styles.button, backgroundColor: "var(--primary-color)" }}>
              ✉️ Send as PDF
            </button>
          )}
          <button onClick={onClose} style={styles.cancelBtn}>
            Close
          </button>
        </div>

        <div style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "1rem" }}>
          Created: {new Date(note.created_at).toLocaleString()}
        </div>

        <div style={styles.formScrollArea}>
          {/* DATE */}
          <div style={styles.formSection}>
            <div style={styles.formField}>
              <label style={styles.fieldLabel}>Date:</label>
              {editMode ? (
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={styles.input}
                />
              ) : (
                <div style={styles.readOnly}>{date}</div>
              )}
            </div>
          </div>
          
          {/* Contact Type */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Type of Contact:</h3>
            {renderField(
              contactType, 
              setContactType, 
              "select", 
              ["Phone Call", "Email", "Text Message", "Video Call", "In-Person Meeting", "Other"]
            )}
          </div>

          {/* Contact With */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Contact With:</h3>
            {renderField(contactWith, setContactWith, "text")}
          </div>

          {/* Permission */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Client Permission:</h3>
            <div style={styles.checkboxItem}>
              {editMode ? (
                <>
                  <input
                    type="checkbox"
                    id="permissionCheck"
                    checked={permissionToContact}
                    onChange={(e) => setPermissionToContact(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  <label htmlFor="permissionCheck">
                    Client has given permission for this communication
                  </label>
                </>
              ) : (
                <div style={styles.readOnly}>
                  {permissionToContact ? "Client has given permission for this communication" : "Client has NOT given permission for this communication"}
                </div>
              )}
            </div>
          </div>

          {/* Duration */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Duration:</h3>
            {renderField(contactDuration, setContactDuration, "text")}
          </div>

          {/* Purpose */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Purpose of Contact:</h3>
            {renderField(contactPurpose, setContactPurpose, "textarea")}
          </div>

          {/* Summary */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Summary of Contact:</h3>
            {renderField(contactSummary, setContactSummary, "textarea")}
          </div>

          {/* Follow-up */}
          <div style={styles.formSection}>
            <h3 style={styles.sectionHeader}>Follow-up Required:</h3>
            {renderField(followUp, setFollowUp, "textarea")}
          </div>
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>
              Save Changes
            </button>
            <button 
              onClick={() => setEditMode(false)} 
              style={styles.cancelBtn}
            >
              Cancel Edits
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles for the modals
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "700px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  clientInfo: {
    marginBottom: "1rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "1rem"
  },
  formScrollArea: {
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px"
  },
  formSection: {
    marginBottom: "1.5rem",
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    border: "1px solid var(--border-color)"
  },
  fieldGroup: {
    marginBottom: "1rem"
  },
  sectionHeader: {
    fontSize: "1rem",
    fontWeight: "bold",
    margin: "0 0 0.75rem 0",
    color: "var(--text-strong)",
    borderBottom: "1px solid var(--border-color)",
    paddingBottom: "0.25rem"
  },
  textArea: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    resize: "vertical",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  select: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    backgroundColor: "#fff",
    fontSize: "0.9rem"
  },
  input: {
    width: "100%",
    padding: "0.5rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    fontSize: "0.9rem",
    boxSizing: "border-box"
  },
  smallButton: {
    padding: "0.25rem 0.5rem",
    fontSize: "0.8rem",
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dateInput: {
    padding: "0.25rem",
    border: "1px solid var(--border-color)",
    borderRadius: "3px",
    marginLeft: "0.5rem"
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    padding: "0.5rem",
    backgroundColor: "#fff",
    borderRadius: "4px",
    border: "1px solid var(--border-color)"
  },
  readOnly: {
    padding: "0.5rem",
    backgroundColor: "var(--surface-muted)",
    borderRadius: "4px",
    minHeight: "1rem"
  },
  formField: {
    marginBottom: "0.75rem"
  },
  fieldLabel: {
    fontWeight: "bold",
    marginBottom: "0.25rem",
    display: "block"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "var(--success-color)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "var(--text-strong)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "var(--primary-color)",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  }
};

export { CollateralContactModal, CollateralContactViewModal };